import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';

import { useLanguage } from '../../Context/LanguageContext';
import { useTheme } from '../../Context/ThemeContext';
import { lightTheme, darkTheme } from '../../Styles/theme';

import FetchModule from '../../Model/Share/Network/FetchModule';
import { callingCodeToCountry } from '../../Utils/phoneUtils';

import TotalModalWrapper from '../../Model/Share/Normal/TotalModalWrapper';
import CountryCodeSelectBox from '../../Component/Common/CountryCodeSelectBox';

export default function MainPhoneEditModal(props) {
  const { phone, phoneCountryCode, onClickExit, onClickSave } = props;

  const history = useHistory();
  const { t } = useLanguage();
  const { isDark } = useTheme();
  const theme = isDark ? darkTheme : lightTheme;

  const [selectedCountry, setSelectedCountry] = useState({ countryCode: phoneCountryCode || '+82' });
  const [currentCountry, setCurrentCountry] = useState(null);
  const [newPhone, setNewPhone] = useState('');
  const [lookupLinkAppId, setLookupLinkAppId] = useState(undefined);
  // 조회 결과 있을 때만 라벨 표시. undefined(확인 전)은 빈 공간만 유지 //
  const hasLookupResult = lookupLinkAppId !== undefined;

  // selectedCountry가 flagUrl을 가진 완전한 객체로 채워지는 첫 시점에 currentCountry로 1회 복사 //
  useEffect(() => {
    if (currentCountry !== null) return;
    if (!selectedCountry?.flagUrl) return;

    setCurrentCountry(selectedCountry);
  }, [selectedCountry, currentCountry]);

  // 번호 또는 국가가 바뀌면 이전 조회 결과는 무효 - 다시 확인 누르도록 미조회 상태로 되돌림 //
  useEffect(() => {
    setLookupLinkAppId(undefined);
  }, [newPhone, selectedCountry]);

  // 확인 버튼 - 입력한 번호로 링코앱 가입 여부 조회 //
  function onClickConfirm() {
    if (!newPhone || newPhone === '') {
      alert(t('myStoreSetting.alert.phoneRequired'));
      return;
    }

    const requestData = {
      phone: newPhone,
      countryIso: callingCodeToCountry[selectedCountry.countryCode]
    };

    const fetchModule = new FetchModule();
    (
      async () => {
        const responseData = await fetchModule.postDataVersion2('account/link_app/find', 'POST', requestData);

        if (responseData.status === 200) setLookupLinkAppId(responseData.data?.linkAppId ?? null);
        else if (responseData.status === 201) setLookupLinkAppId(null);
        else if (responseData.status === 401) history.push('/login');
        else alert(t('myStoreSetting.alert.serverError'));
      }
    )();
  }

  // 번호 변경 - 부모가 API 호출 후 성공 시 모달을 닫음 (콜백 미지정 시는 즉시 닫기) //
  function onClickSaveButton() {
    if (onClickSave) {
      onClickSave(selectedCountry.countryCode, newPhone);
      return;
    }

    onClickExit();
  }

  return (
    <TotalModalWrapper>
      <Modal backgroundColor={theme.subBackground}>
        <TitleWrapper>
          <Text
            fontFamily='NotoSansKR-Bold'
            fontSize={16}
            color={theme.text}>
            {t('myStoreSetting.account.phoneEditModal.title')}
          </Text>
          <Text
            fontFamily='NotoSansKR-Regular'
            fontSize={12}
            color={theme.gray}>
            {t('myStoreSetting.account.phoneEditModal.description')}
          </Text>
        </TitleWrapper>

        <FieldGroup>
          <Text
            fontFamily='NotoSansKR-Medium'
            fontSize={14}
            color={theme.text}>
            {t('myStoreSetting.account.phoneEditModal.currentLabel')}
          </Text>
          <CurrentRow>
            <CurrentFlagBox backgroundColor={theme.subBackground}>
              {
                currentCountry?.flagUrl &&
                <CountryFlagImage src={currentCountry.flagUrl} alt='' />
              }
            </CurrentFlagBox>
            <DisabledBox borderColor={theme.line} backgroundColor={theme.subBackground}>
              <Text
                fontFamily='NotoSansKR-Regular'
                fontSize={14}
                color={theme.text}>
                {phone || ''}
              </Text>
            </DisabledBox>
          </CurrentRow>
        </FieldGroup>

        <FieldGroup>
          <Text
            fontFamily='NotoSansKR-Medium'
            fontSize={14}
            color={theme.text}>
            {t('myStoreSetting.account.phoneEditModal.newLabel')}
          </Text>
          <NewRow>
            <CountryCodeSelectBox
              selectedCountry={selectedCountry}
              setSelectedCountry={setSelectedCountry} />
            <InputBox borderColor={theme.line} backgroundColor={theme.subBackground}>
              <InputField
                color={theme.text}
                placeholderColor={theme.bottomText}
                type='tel'
                value={newPhone}
                onChange={(e) => setNewPhone(e.target.value.replace(/[^0-9]/g, ''))}
                placeholder={t('myStoreSetting.account.phoneEditModal.placeholder')} />
            </InputBox>
            <ConfirmButton backgroundColor={theme.subKey} onClick={onClickConfirm}>
              <Text
                fontFamily='NotoSansKR-Medium'
                fontSize={14}
                color={theme.subBackground}>
                {t('myStoreSetting.account.phoneEditModal.confirm')}
              </Text>
            </ConfirmButton>
          </NewRow>
          {
            hasLookupResult &&
            <LinkoAppRow>
              <Text
                fontFamily='NotoSansKR-Regular'
                fontSize={14}
                color={lookupLinkAppId ? theme.text : theme.pointRed}>
                {t('myStoreSetting.account.phoneEditModal.linkoAppPrefix')}
              </Text>
              <Text
                fontFamily='NotoSansKR-Regular'
                fontSize={14}
                color={lookupLinkAppId ? theme.text : theme.pointRed}>
                {lookupLinkAppId ? lookupLinkAppId : t('myStoreSetting.account.phoneEditModal.linkoAppNotJoined')}
              </Text>
            </LinkoAppRow>
          }
        </FieldGroup>

        <ButtonRow>
          <CloseButton backgroundColor={theme.buttonFill} onClick={onClickExit}>
            <Text
              fontFamily='NotoSansKR-Medium'
              fontSize={14}
              color={theme.text}>
              {t('myStoreSetting.account.phoneEditModal.close')}
            </Text>
          </CloseButton>
          <SaveButton backgroundColor={theme.primary} onClick={onClickSaveButton}>
            <Text
              fontFamily='NotoSansKR-Medium'
              fontSize={14}
              color={theme.subBackground}>
              {t('myStoreSetting.account.phoneEditModal.save')}
            </Text>
          </SaveButton>
        </ButtonRow>
      </Modal>
    </TotalModalWrapper>
  );
}

const Modal = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: stretch;

  gap: 10px;

  width: 355px;
  padding: 20px;
  box-sizing: border-box;

  border-radius: 10px;
  background-color: ${props => props.backgroundColor};
`;

const TitleWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: stretch;

  gap: 10px;

  padding-bottom: 20px;

  width: 100%;
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: stretch;

  gap: 8px;

  padding-bottom: 10px;

  width: 100%;
`;

const CurrentRow = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: center;

  gap: 8px;

  width: 100%;
`;

const NewRow = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: center;

  gap: 8px;

  width: 100%;
`;

const CurrentFlagBox = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: flex-start;

  width: 70px;
  height: 34px;
  padding: 10px 5px;
  box-sizing: border-box;

  border-radius: 5px;
  background-color: ${props => props.backgroundColor};

  flex-shrink: 0;
`;

const CountryFlagImage = styled.img`
  width: 32px;
  height: 22px;

  border-radius: 3px;
  object-fit: cover;
`;

const DisabledBox = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: center;

  flex: 1 0 0;
  min-width: 0;

  height: 34px;
  padding: 0 10px;
  box-sizing: border-box;

  border-radius: 5px;
  border: 1px solid ${props => props.borderColor};
  background-color: ${props => props.backgroundColor};
`;

const InputBox = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: center;

  flex: 1 0 0;
  min-width: 0;

  height: 34px;
  padding: 0 10px;
  box-sizing: border-box;

  border-radius: 5px;
  border: 1px solid ${props => props.borderColor};
  background-color: ${props => props.backgroundColor};
`;

const InputField = styled.input`
  font-family: NotoSansKR-Regular;
  font-size: 14px;

  outline: none;
  border: 0px;

  width: 100%;
  height: 100%;

  color: ${props => props.color};
  background-color: transparent;

  ::placeholder {
    color: ${props => props.placeholderColor};
  }
`;

const ConfirmButton = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 56px;
  height: 34px;

  border-radius: 5px;
  background-color: ${props => props.backgroundColor};

  flex-shrink: 0;

  @media (hover: hover) {
    &:hover {
      cursor: pointer;
    }
  }
`;

const LinkoAppRow = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-end;
  align-items: center;

  gap: 4px;

  width: 100%;
`;

const ButtonRow = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: stretch;
  align-items: center;

  gap: 8px;

  width: 100%;

  margin-top: 40px;
`;

const CloseButton = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 72px;
  height: 44px;

  border-radius: 10px;
  background-color: ${props => props.backgroundColor};

  flex-shrink: 0;

  @media (hover: hover) {
    &:hover {
      cursor: pointer;
    }
  }
`;

const SaveButton = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  flex: 1 0 0;
  height: 44px;

  border-radius: 10px;
  background-color: ${props => props.backgroundColor};

  @media (hover: hover) {
    &:hover {
      cursor: pointer;
    }
  }
`;

const Text = styled.p`
  font-family: ${props => props.fontFamily};
  font-size: ${props => props.fontSize}px;

  color: ${props => props.color};
  background-color: ${props => props.backgroundColor || 'transparent'};

  margin: 0 0 0 0;
`;